import { Logger, LoggerService, LogLevels } from "@Shared/logger";
import { Auth0Options, AuthMode } from "./_models";
import { OptionsValidator, StorageUtil } from "@Shared/utils";
import { DisplayType } from "@Modules/ui_module/display/models";
import { Auth0Service } from "./services/auth0.service";
import { AuthCompatService } from "./services/auth-compat.service";
import { PipelineModule } from "@Modules/pipeline_module";
import { OptionsModule } from "@Modules/options_module";
import { CheckAuthStatusStep } from "@Modules/pipeline_module/steps/auth/check-auth-status.step";
import { ScreenManager } from "@Modules/ui_module/screen-manager/screen-manager";
import { AmpRegwallStep, ClearUserDataStep, ParseAuthRedirectStep, PreAppleLoginStep } from "@Modules/pipeline_module/steps";
import { PipelineStep } from "@Modules/pipeline_module/_base";
import { ClearEntitlementCacheStep } from "@Modules/pipeline_module/steps/entitlement/clear-entitlement-cache.step";
import { AppleService } from "@Modules/apple_module";
import { EntitlementService } from "@Modules/entitlement_module/services/entitlement.service";
import { AuthenticationUtils } from "./utils/auth.utils";
import { STORAGE } from "../../core/models/storage.const";


export class AuthModuleClass {
    private Logger: Logger = LoggerService.createLogger("AuthModule");
    private auth0Service: Auth0Service;
    private auth0Options: Auth0Options;
    get AuthOptions(): Auth0Options {
        return this.auth0Options;
    }

    constructor() {
        this.Logger.trace("****  CTOR ****");
        this.auth0Service = Auth0Service.Instance;
    }

    public async init(opts: Auth0Options): Promise<void> {
        this.Logger.trace("Auth Initializing");
        try {

            this.setDefaults(opts);
            AuthCompatService.setup();

            return Promise.resolve();
        } catch (error) {
            return Promise.reject(error);


        }

    }

    start(isManual: boolean = false): void {
        try {
            this.Logger.trace("start");
            this.handleStart(isManual);
        } catch (error) {

        }
    }

    private handleStart(isManual: boolean): void {
        try {
            this.Logger.trace("handleStart");
            ScreenManager.Instance.setup(this.auth0Options);
            
            if (this.shouldStart(isManual)) {
                //we either have autoRun to true or we are manually starting the provider.

                PipelineModule.startPipeline(this.determineFirstStep());
            }



        } catch (error) {
            this.Logger.error("Error in handleStart", error);
            
        }
    }

    private determineFirstStep(): PipelineStep {
        this.Logger.trace("determineFirstStep");
        const shouldClear = this.auth0Service.shouldClearUser;
        this.Logger.trace("shouldClearUser", shouldClear);
        if(this.auth0Service.shouldClearUser) {
            return new ClearUserDataStep();
        }

        if(this.auth0Service.shouldHandleLogoutPath) {
            this.Logger.trace("on /logout path, running logout");
            return new ClearUserDataStep();
        }

        if(AppleService.Instance.shouldHandlePreAppleLogin) {
            this.Logger.trace("Apple News pre-login URL detected, routing to PreAppleLoginStep");
            return new PreAppleLoginStep();
        }

        if(this.auth0Service.shouldParseResult) {
            return new ParseAuthRedirectStep();
        }

        if(this.auth0Service.shouldStartAmpRegwall) {
            return new AmpRegwallStep();
        }

        if(this.auth0Service.shouldClearEntitlementsCache) {
            return new ClearEntitlementCacheStep();
        }


        return new CheckAuthStatusStep();
    }

    private shouldStart(isManual: boolean = false): boolean {
        try {
            const shouldStart = (isManual || OptionsModule.Options.autoRun);
            this.Logger.trace("shouldStart returning => ", shouldStart, "isManual", isManual, "autoRun", OptionsModule.Options.autoRun);
            return shouldStart;

        } catch (error) {
            return false;
        }
    }

    private setDefaults(opts: Auth0Options): void {
        try {
            const defaultValues: Partial<Auth0Options> = {
                callbackURL: window.location.origin + '/callback',
                domain: 'login.npuserlogin.com',
                isTrib: false,
                logout: {
                    redirectUri:window.location.origin,
                    clearServerCache: true
                },
                checkSessionCache: {
                    useCache: true,
                    cacheOptions: {
                        storage: 'localStorage',
                        duration: {
                            type: 'today'
                        }
                    }
                },
                mode: {
                    type: AuthMode.Embedded,
                    options: {
                        display: {
                            type: DisplayType.Modal,

                        },
                        reloadOnAuth: true
                    }
                    
                },
                useSessionServer: false
            };

            const requiredKeys = {
                clientId: true

            };

            const validatedOpts = new OptionsValidator<Auth0Options>(
                opts, // User-provided options
                defaultValues,
                requiredKeys
            );
            this.auth0Options = validatedOpts.getOptions();
            this.auth0Service.setup(this.auth0Options);
        } catch (error) {
            throw error;
        }

    }

    /**
     * Logout flow matching requirements (no logout page, clears cookies, cache, storage, callbacks, Connext, Auth0, etc.)
     */
    public async logout(): Promise<void> {
        this.Logger.trace('Attempting to logout...');

        AuthenticationUtils.removeSSPaywallCookie();

        // Delete user cache before logout redirects and local storage is cleared.
        // This is best-effort cleanup: a failure here (e.g. no user yet, network error)
        // must NOT abort the logout, otherwise onLogout never fires and we never redirect.
        this.Logger.trace('About to perform user cache deletion...');
        try {
            const response = await EntitlementService.Instance.clearCachedEntitlements();
            if (response === true) {
                this.Logger.trace('User cache cleared.');
            } else {
                this.Logger.trace('User cache deletion failed.');
            }
        } catch (e) {
            this.Logger.trace('User cache deletion threw, continuing logout: ', e);
        }

        try {
            this.Logger.trace('Cycling through logout callbacks...');
            const arrayOfPromises = window.MNGAuthentication?.logoutCallbacks?.map((task: any) => task()) || [];
            await Promise.all(arrayOfPromises);
            this.Logger.trace('Finished cycling through logout callbacks.');
        } catch (e) {
            this.Logger.trace('Failed to cycle through logout callbacks: ', e);
        }

        // Remove other storage objects
        StorageUtil.deleteStorageObject('hhsl');
        StorageUtil.deleteStorageObject('userNewsLetterData');
        StorageUtil.deleteStorageObject(STORAGE.regwallUserIsSubscribed);

        // Integrate with Connext logout.
        // Pass an explicit, bounded timeout (ms)
        const CONNEXT_READY_TIMEOUT_MS = 500;
        const connextLoaded = await window.ConnextUtils?.connextReady?.('onInit', CONNEXT_READY_TIMEOUT_MS);
        if (connextLoaded) {
            this.Logger.trace('Auth | Connext silent mode enabled ', window.Connext.GetOptions().Silentmode);
            this.Logger.trace('Auth | Run connext manually if Silent Mode is on.');
            window.ConnextUtils.rerunConnextEntitlements(true);
            await AuthenticationUtils.callConnextLogoutWithTimeout();
        } else {
            this.Logger.trace('Connext Logout was NOT called');
        }
        this.Logger.trace('Calling Auth0 logout now.');
        await this.auth0Service.logout();
    }

}

export const AuthModule = new AuthModuleClass();